import type { WorkflowDefinition, WorkflowStepSignalContext } from "../types";

/** The location child this run is about, once it is bound. */
function boundLocation(ctx: WorkflowStepSignalContext) {
	const id = ctx.run?.context.locationPropertyId;
	if (!id) {
		return null;
	}
	return (
		ctx.state.property?.locationChildren.find((child) => child.id === id) ??
		null
	);
}

/**
 * W7 — Expand into a new market.
 *
 * `perTarget` on a location: a brand entering three cities runs this three
 * times, and each run has to be about one market or the steps would tick each
 * other off. The market usually doesn't exist yet when the run starts (adding
 * it *is* step one), so the run starts unbound and binds from the step panel
 * once a location child exists.
 *
 * Two steps route to `location:` pages. Those resolve against the *child*
 * property, because the competitor list and the prompt set that matter here
 * belong to the market, not to the parent — the whole point of the playbook is
 * that they differ. Until the run is bound there is no child to link to, so the
 * runner disables those CTAs and offers the candidate list instead.
 */
export const marketExpansionWorkflow: WorkflowDefinition = {
	id: "market-expansion",
	wNumber: 7,
	title: "Measure AI search visibility in a new market",
	outcome:
		"Measure a new city or country on its own numbers instead of a national average.",
	audiences: ["agencies", "inhouse"],
	uses: [
		"Locations",
		"Competitors",
		"Prompts",
		"Projects",
		"Locations dashboard",
	],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "location", label: "Market" },
	// LOCATION children hang off a BRAND or a PRODUCT; a PERSON has no market
	// to expand into and a LOCATION is already one.
	isApplicable: (mode) => mode.canHaveLocations,
	articleSlug: "how-to-measure-ai-visibility-in-a-new-market",
	articleStatus: "published",
	caveats: [
		"Every market tracks its own prompts and is billed for them. Be sure to decide how many prompts to track in each market to better balance accuracy and cost.",
	],
	status: "live",
	steps: [
		{
			id: "add-location",
			title: "Add the new market",
			page: "Locations",
			route: "/locations",
			criterion:
				"You've added the market, or chosen the one this workflow is for.",
			why: "A national number is mostly the market the brand already owns. A strong score can hide a new city completely.",
			docsHref: "/docs/dashboards/locations",
			tasks: [
				"Pick the smallest unit the business really works in",
				"Search for it and give it the name people will recognize",
				"Set it up before launch if you can",
			],
			// Either half satisfies it: a market added for this run, or a run
			// bound to a market that already existed. A location child sitting on
			// the property that nobody has pointed this run at does not.
			complete: {
				kind: "any",
				of: [
					{ kind: "since", event: "locationAdded" },
					{ kind: "target" },
				],
			},
			spend: {
				kind: "trackedPrompts",
				count: "fromState",
				note: "Each market tracks its own prompts and is billed for them.",
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Markets tracked",
							value: state.property.locationChildCount,
							target: 1,
						}
					: null,
		},
		{
			id: "localize-competitors",
			title: "Rebuild the competitor set for that market",
			page: "Competitors",
			route: "location:/competitors",
			criterion: "This market has its own competitors.",
			why: "The head-office list names paper rivals and misses the local operator who owns every local roundup. Local answers name different brands.",
			docsHref: "/docs/dashboards/competitors",
			tasks: [
				"Remove competitors who do not operate in this market",
				"Add the local brands the market's answers name",
				"Add short names, nicknames and former names",
			],
			// Reads the bound market's own competitor count and stays incomplete
			// until the run is bound, so it never completes off a different
			// city's work and never reads DONE beside the market picker.
			complete: { kind: "locations", bound: "hasOwnCompetitors" },
			signal: (ctx) => {
				const bound = boundLocation(ctx);
				if (bound) {
					return {
						kind: "count",
						label: `Competitors tracked in ${bound.label}`,
						value: bound.competitorCount,
						target: 1,
					};
				}
				const children = ctx.state.property?.locationChildren ?? [];
				if (children.length === 0) {
					return null;
				}
				return {
					kind: "checklist",
					items: children.map((child) => ({
						label: `${child.label}: ${child.competitorCount} competitors`,
						done: child.competitorCount > 0,
					})),
				};
			},
		},
		{
			id: "review-prompts",
			title: "Check the prompts fit the market",
			page: "Prompts",
			route: "location:/discovery-queries",
			criterion: "You've read the prompts this market inherited.",
			why: "Prompts arrive from the parent brand with the place name dropped in. That is a starting point, not a finished set.",
			docsHref: "/docs/dashboards/discovery-queries",
			tasks: [
				"Read every inherited prompt as a local buyer would",
				"Cut prompts about products not sold there",
				"For a new country, use the language buyers really ask in",
				"Tag the set with the market name",
			],
			// Prompt counts in the loader are property-wide, so there is no
			// per-market number to show here rather than invent one.
			complete: { kind: "manual" },
		},
		{
			id: "market-goals",
			title: "Pick local goals worth measuring",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've added a publisher or coverage group goal.",
			why: "Local outlets usually beat national ones here, because AI looks for local sources. Score them before you spend outreach hours.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Shortlist the local outlets and the brand's city pages",
				"Score the outlets before committing outreach hours",
				"Attach the shortlist to a project as goals",
			],
			optional: true,
			complete: {
				kind: "any",
				of: [
					{ kind: "count", key: "goalTypes.PUBLISHER", gte: 1 },
					{ kind: "count", key: "goalTypes.COVERAGE_GROUP", gte: 1 },
				],
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: `${state.property.goalTypes.PUBLISHER} publisher goals`,
									done:
										state.property.goalTypes.PUBLISHER > 0,
								},
								{
									label: `${state.property.goalTypes.COVERAGE_GROUP} coverage group goals`,
									done:
										state.property.goalTypes
											.COVERAGE_GROUP > 0,
								},
							],
						}
					: null,
		},
		{
			id: "watch-the-market",
			title: "Read the market on its own line",
			page: "Locations",
			route: "/dashboard/locations",
			criterion: "You've got data for each market.",
			why: "Chart the new market next to the home market. When only the new line moves, your local work did it.",
			docsHref: "/docs/dashboards/locations",
			tasks: [
				"Compare the market's share of voice against the home market",
				"Note local placements and page launches as they happen",
				"Read the citations underneath for your next outreach list",
			],
			complete: { kind: "flag", key: "hasLocationMetrics" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Data flowing for each market",
									done: state.property.hasLocationMetrics,
								},
								{
									label: `${state.property.locationChildCount} markets on the chart`,
									done: state.property.locationChildCount > 0,
								},
							],
						}
					: null,
		},
	],
};
